// src/hooks/useTipLimitNotices.js
// Purchase-limit state for a Super Crit panel (STEP3_5A_TIERS_DESIGN.md 3.7;
// release R3, dark in 'cap' mode). StreamWatchScreen and TeamStreamScreen use
// it; StreamRumble and TsunamiMeter keep the same state inline.
//
// Everything here stays null unless the server sent 'limits'-mode data:
//  - limitErr     { limit, amountCents }: a tips-limit refusal's details.limit
//                 (L2-L4 from its numbers);
//  - cardRefusal  'card_limit' | 'card_blocked': a one-tap card refusal, shown
//                 (L5 / L6) above the embedded checkout it falls through to;
//  - confirm      'confirming' | 'pending': after an embedded checkout whose
//                 create said cardCheck: true, while getTipPurchaseOutcome is
//                 polled (L9);
//  - outcomeLine  L7 / L8 / L8b when that held payment was stopped.
//
// verify: the setup limit's verify state, read when an outcome line is built.

import { useCallback, useEffect, useRef, useState } from 'react';
import { pollTipPurchaseOutcome } from '../utils/rumblePayment';
import { captureOutcomeLine } from '../utils/spendingLimit';

export default function useTipLimitNotices(verify) {
  const verifyRef = useRef(verify);
  verifyRef.current = verify;
  const [limitErr, setLimitErr] = useState(null);
  const [cardRefusal, setCardRefusal] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [outcomeLine, setOutcomeLine] = useState(null);
  // Bumps on reset and unmount: a poll that settles after either is dropped.
  const seqRef = useRef(0);
  useEffect(() => () => { seqRef.current += 1; }, []);

  // Before a new send: the last refusal's numbers and outcome go.
  const clearNotices = useCallback(() => {
    setLimitErr(null);
    setOutcomeLine(null);
  }, []);

  // Panel opened or closed.
  const reset = useCallback(() => {
    seqRef.current += 1;
    setLimitErr(null);
    setCardRefusal(null);
    setConfirm(null);
    setOutcomeLine(null);
  }, []);

  // kind: 'super_crit' | 'team_super_crit'. onCompleted: today's sent state.
  // onFailed: a stop with no card or account reason (the panel's own
  // failure text).
  const confirmHeld = useCallback((kind, purchaseId, { onCompleted, onFailed } = {}) => {
    const seq = ++seqRef.current;
    const alive = () => seqRef.current === seq;
    setConfirm('confirming');
    pollTipPurchaseOutcome(kind, purchaseId, { isAlive: alive }).then(({ status, reason }) => {
      if (!alive()) return;
      if (status === 'completed') {
        setConfirm(null);
        if (onCompleted) onCompleted();
        return;
      }
      if (status === 'failed') {
        setConfirm(null);
        const line = captureOutcomeLine(reason, { verify: verifyRef.current });
        if (line) setOutcomeLine(line);
        else if (onFailed) onFailed();
        return;
      }
      setConfirm('pending');
    });
  }, []);

  return {
    limitErr, setLimitErr,
    cardRefusal, setCardRefusal,
    confirm,
    outcomeLine,
    clearNotices, reset, confirmHeld,
  };
}
